import React, { useState } from 'react';
import { Card, CardContent } from './ui/Card';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { Instagram, Facebook, Youtube, Twitter, Music, CheckCircle2, Loader2 } from 'lucide-react';
export type PlatformType = 'instagram' | 'facebook' | 'youtube' | 'twitter' | 'tiktok';
export interface Platform {
  id: PlatformType;
  name: string;
  isConnected: boolean;
}
interface PlatformCardProps {
  platform: Platform;
  onConnect: (id: PlatformType) => Promise<void>;
}
export function PlatformCard({
  platform,
  onConnect
}: PlatformCardProps) {
  const [isLoading, setIsLoading] = useState(false);
  const handleConnect = async () => {
    setIsLoading(true);
    try {
      await onConnect(platform.id);
    } finally {
      setIsLoading(false);
    }
  };
  const getIcon = () => {
    switch (platform.id) {
      case 'instagram':
        return <Instagram className="h-8 w-8" data-id="element-276" />;
      case 'facebook':
        return <Facebook className="h-8 w-8" data-id="element-277" />;
      case 'youtube':
        return <Youtube className="h-8 w-8" data-id="element-278" />;
      case 'twitter':
        return <Twitter className="h-8 w-8" data-id="element-279" />;
      case 'tiktok':
        return <Music className="h-8 w-8" data-id="element-280" />;
    }
  };
  const getColors = () => {
    switch (platform.id) {
      case 'instagram':
        return 'text-pink-600 bg-pink-50 border-pink-100';
      case 'facebook':
        return 'text-blue-600 bg-blue-50 border-blue-100';
      case 'youtube':
        return 'text-red-600 bg-red-50 border-red-100';
      case 'twitter':
        return 'text-gray-900 bg-gray-50 border-gray-200';
      case 'tiktok':
        return 'text-cyan-900 bg-cyan-50 border-cyan-100';
    }
  };
  return <Card className={`border-2 ${platform.isConnected ? 'border-green-100' : 'border-gray-100'}`} data-id="element-281">
      <CardContent className="p-6 flex flex-col items-center text-center" data-id="element-282">
        <div className={`h-16 w-16 rounded-2xl flex items-center justify-center mb-4 ${getColors()}`} data-id="element-283">
          {getIcon()}
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-284">
          {platform.name}
        </h3>

        <Badge variant={platform.isConnected ? 'success' : 'secondary'} className="mb-6" data-id="element-285">
          {platform.isConnected ? <span className="flex items-center" data-id="element-286">
              <CheckCircle2 className="h-3 w-3 mr-1" data-id="element-287" /> Connected
            </span> : 'Not Connected'}
        </Badge>

        <Button variant={platform.isConnected ? 'secondary' : 'gradient'} className="w-full" onClick={handleConnect} disabled={isLoading} data-id="element-288">
          {isLoading ? <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" data-id="element-289" /> Connecting...
            </> : platform.isConnected ? 'Manage Connection' : 'Connect Account'}
        </Button>
      </CardContent>
    </Card>;
}